import React from 'react';
import { Navbar as BootstrapNavbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useAuth } from '../contexts/AuthContext';

/**
 * Navigation Bar component for the BagABoard application.
 * 
 * This component provides the main navigation interface with different
 * menu items based on user authentication status and role.
 * 
 * Features:
 * - Responsive design with Bootstrap
 * - Authentication-aware menu items
 * - User dropdown with profile and logout options
 * - Brand logo and navigation links
 * 
 * @author BagABoard Team
 */
function Navbar() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  /**
   * Handle user logout
   */
  const handleLogout = () => {
    logout();
  };

  return (
    <BootstrapNavbar bg="white" expand="lg" className="shadow-sm" fixed="top">
      <Container>
        {/* Brand Logo */}
        <LinkContainer to="/">
          <BootstrapNavbar.Brand className="fw-bold fs-3">
            <i className="fas fa-suitcase-rolling me-2 text-primary"></i>
            BagAboard
          </BootstrapNavbar.Brand>
        </LinkContainer>

        {/* Mobile Menu Toggle */}
        <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />

        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          {/* Main Navigation Links */}
          <Nav className="me-auto">
            <LinkContainer to="/">
              <Nav.Link>
                <i className="fas fa-home me-1"></i>
                Home
              </Nav.Link>
            </LinkContainer>

            <LinkContainer to="/track">
              <Nav.Link>
                <i className="fas fa-search me-1"></i>
                Track Booking
              </Nav.Link>
            </LinkContainer>

            <LinkContainer to="/about">
              <Nav.Link>
                <i className="fas fa-info-circle me-1"></i>
                About
              </Nav.Link>
            </LinkContainer>

            <LinkContainer to="/contact">
              <Nav.Link>
                <i className="fas fa-envelope me-1"></i>
                Contact
              </Nav.Link>
            </LinkContainer>
          </Nav>

          {/* Authentication-based Navigation */}
          <Nav>
            {isAuthenticated() ? (
              // Authenticated User Menu
              <>
                <LinkContainer to="/route-prices">
                  <Nav.Link className="btn btn-primary text-white me-2 px-3 rounded">
                    <i className="fas fa-plus me-1"></i>
                    Book Now
                  </Nav.Link>
                </LinkContainer>

                {/* User Dropdown Menu */}
                <NavDropdown
                  title={
                    <span>
                      <i className="fas fa-user-circle me-1"></i>
                      {user?.fullName || 'User'}
                    </span>
                  }
                  id="user-dropdown"
                  align="end"
                >
                  <LinkContainer to="/dashboard">
                    <NavDropdown.Item>
                      <i className="fas fa-tachometer-alt me-2"></i>
                      Dashboard
                    </NavDropdown.Item>
                  </LinkContainer>

                  <NavDropdown.Divider />

                  <NavDropdown.Item>
                    <i className="fas fa-user me-2"></i>
                    Profile
                  </NavDropdown.Item>

                  <NavDropdown.Item>
                    <i className="fas fa-cog me-2"></i>
                    Settings
                  </NavDropdown.Item>

                  {/* Admin-only menu items */}
                  {isAdmin() && (
                    <>
                      <NavDropdown.Divider />
                      <NavDropdown.Item>
                        <i className="fas fa-users-cog me-2"></i>
                        Admin Panel
                      </NavDropdown.Item>
                    </>
                  )}

                  <NavDropdown.Divider />

                  <NavDropdown.Item onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt me-2"></i>
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              // Guest User Menu
              <>
                <LinkContainer to="/login">
                  <Nav.Link>
                    <i className="fas fa-sign-in-alt me-1"></i>
                    Login
                  </Nav.Link>
                </LinkContainer>

                <LinkContainer to="/register">
                  <Nav.Link className="btn btn-primary text-white ms-2 px-3 rounded">
                    <i className="fas fa-user-plus me-1"></i>
                    Sign Up
                  </Nav.Link>
                </LinkContainer>
              </>
            )}
          </Nav>
        </BootstrapNavbar.Collapse>
      </Container>
    </BootstrapNavbar>
  );
}

export default Navbar;
